import { nextCycleReset } from '@xver/utils/date';
import { useTranslation } from 'react-i18next';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { format } from '../../../../../utils/number';
import { useInstanceCostQuery } from '../hooks/useInstanceCostQuery';

interface CostCardProps {
    instanceId: number;
}

const SEGMENT_COLORS = {
    compute: '#3b82f6',
    backup: '#a78bfa',
    bandwidth: '#14b8a6',
} as const;

function LegendDot({ color }: { color: string }) {
    return (
        <span
            className="inline-block w-2 h-2 rounded-sm"
            style={{ backgroundColor: color }}
            aria-hidden
        />
    );
}

export default function CostCard({ instanceId }: CostCardProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const { data, isPending } = useInstanceCostQuery(instanceId);

    const loading = isPending && !data;
    const hourlyTotal = data?.hourly.total;
    const estimatedMonthly = data?.monthly.estimated ?? data?.monthly.total;
    const spentThisMonth = data?.monthly.total;
    const share = data?.share ?? { compute: 0, backup: 0, bandwidth: 0 };
    const shareTotal = share.compute + share.backup + share.bandwidth;
    const cycleResetLabel = nextCycleReset(data?.monthly.period);

    const footer = (
        <div className="flex flex-col gap-1 text-sm">
            <div className="flex justify-between items-center">
                <span className="text-fg/68">{t('cost.spentThisMonth')}</span>
                <span className="text-fg">฿{loading ? '-' : format(spentThisMonth, 2)}</span>
            </div>
            <div className="flex justify-between items-center">
                <span className="text-fg/68">{t('cost.cycleResets')}</span>
                <span className="text-fg">{loading ? '-' : cycleResetLabel}</span>
            </div>
        </div>
    );

    return (
        <DialogBox
            label={t('cost.title')}
            description={t('cost.description')}
            classNames="p-4 gap-4"
            footer={footer}
        >
            <div className="w-full flex flex-row justify-between items-start gap-4">
                <div className="flex flex-col items-start">
                    <span className="text-sm text-fg/68">{t('cost.hourly')}</span>
                    <div className="text-2xl font-semibold text-fg">
                        ฿{loading ? '-' : format(hourlyTotal, 2)}
                        <span className="text-base text-fg/68" style={{ fontWeight: 400 }}>
                            {' '}
                            {t('cost.perHourSuffix')}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col items-end">
                    <span className="text-sm text-fg/68">{t('cost.estMonthly')}</span>
                    <div className="text-2xl font-semibold text-fg">
                        ฿{loading ? '-' : format(estimatedMonthly, 2)}
                    </div>
                </div>
            </div>

            <div className="w-full h-2 rounded-full bg-surface-3 overflow-hidden flex">
                {shareTotal > 0 && (
                    <>
                        <div
                            style={{
                                width: `${share.compute}%`,
                                backgroundColor: SEGMENT_COLORS.compute,
                            }}
                        />
                        <div
                            style={{
                                width: `${share.backup}%`,
                                backgroundColor: SEGMENT_COLORS.backup,
                            }}
                        />
                        <div
                            style={{
                                width: `${share.bandwidth}%`,
                                backgroundColor: SEGMENT_COLORS.bandwidth,
                            }}
                        />
                    </>
                )}
            </div>

            <div className="w-full flex flex-wrap gap-x-6 gap-y-2 text-sm text-fg">
                <div className="flex items-center gap-2">
                    <LegendDot color={SEGMENT_COLORS.compute} />
                    <span>
                        {t('cost.compute')} {Math.round(share.compute)}%
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <LegendDot color={SEGMENT_COLORS.backup} />
                    <span>
                        {t('cost.backup')} {Math.round(share.backup)}%
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <LegendDot color={SEGMENT_COLORS.bandwidth} />
                    <span>
                        {t('cost.bandwidth')} {Math.round(share.bandwidth)}%
                    </span>
                </div>
            </div>
        </DialogBox>
    );
}
